import { computed, makeObservable, observable } from 'mobx';

import { UnreachableError } from 'base/helpers';
import { TypographyTarget } from 'base/styleguide/model';
import {
  EnumHashParameterStore,
  UuidHashParameterStore,
} from 'blocks/hash_parameter';
import { Section } from '../model';
import { HeadingFontCollectionSlug } from './fonts';

export const STYLEGUIDE_VIEW = ['builder', 'preview'] as const;
export type StyleguideView = (typeof STYLEGUIDE_VIEW)[number];

export const styleguideView = new EnumHashParameterStore<StyleguideView>(
  'view',
  STYLEGUIDE_VIEW,
  'builder',
);

export const styleguideConceptId = new UuidHashParameterStore('concept');

type TypographyTab = 'liked' | 'browse';
export type TypographyCategory =
  | 'sans-serif'
  | 'serif'
  | 'display'
  | 'monospace';

export type ColorsRoute =
  | { path: 'colors' }
  | { path: 'colors/neutral' }
  | { path: 'colors/chromatic1' }
  | { path: 'colors/chromatic2' }
  | { path: 'colors/chromatic3' }
  | { path: 'colors/chromatic4' }
  | { path: 'colors/chromatic5' };
export type BrandRoute = { path: 'brand' };
export type TypographyRoute =
  | { path: 'fontFamily'; target: TypographyTarget; tab?: TypographyTab }
  | {
      path: 'fontFamily/search';
      target: TypographyTarget;
      query?: string;
      tab?: TypographyTab;
    }
  | {
      path: 'fontFamily/collection';
      target: 'heading';
      tab?: TypographyTab;
      slug: HeadingFontCollectionSlug;
    }
  | {
      path: 'fontFamily/category';
      target: TypographyTarget;
      tab?: TypographyTab;
      category: TypographyCategory;
    };
export type FeedbackRoute = { path: 'feedback' };
export type ImageryRoute = {
  path: 'imagery';
  section: Section;
};
export type UiStylesRoute = { path: 'uiStyles' };
export type CardStylesRoute = { path: 'cardStyles' };

export type Route =
  | ColorsRoute
  | BrandRoute
  | TypographyRoute
  | FeedbackRoute
  | ImageryRoute
  | UiStylesRoute
  | CardStylesRoute;
export type Toplevel =
  | 'colors'
  | 'brand'
  | 'fontFamily'
  | 'feedback'
  | 'imagery'
  | 'uiStyles'
  | 'cardStyles';

const TOPLEVELS_BY_PATH: Record<Route['path'], Toplevel> = {
  colors: 'colors',
  'colors/neutral': 'colors',
  'colors/chromatic1': 'colors',
  'colors/chromatic2': 'colors',
  'colors/chromatic3': 'colors',
  'colors/chromatic4': 'colors',
  'colors/chromatic5': 'colors',
  brand: 'brand',
  fontFamily: 'fontFamily',
  'fontFamily/search': 'fontFamily',
  'fontFamily/collection': 'fontFamily',
  'fontFamily/category': 'fontFamily',
  feedback: 'feedback',
  imagery: 'imagery',
  uiStyles: 'uiStyles',
  cardStyles: 'cardStyles',
};

export class SidebarNavigation {
  at: Route | undefined = undefined;

  // TODO add observable decorator
  constructor() {
    makeObservable(this, {
      at: observable.ref,
      toplevel: computed,
      back: computed,
    });
  }

  get toplevel(): Toplevel | undefined {
    if (!this.at) return undefined;
    return TOPLEVELS_BY_PATH[this.at.path];
  }

  get back(): (() => void) | undefined {
    const { goesBackTo } = this;
    return goesBackTo ? () => this.goto(goesBackTo) : undefined;
  }

  get goesBackTo(): Route | undefined {
    if (!this.at) return undefined;
    switch (this.at.path) {
      case 'colors':
      case 'colors/neutral':
      case 'colors/chromatic1':
      case 'colors/chromatic2':
      case 'colors/chromatic3':
      case 'colors/chromatic4':
      case 'colors/chromatic5':
      case 'brand':
      case 'fontFamily':
      case 'fontFamily/search':
      case 'feedback':
      case 'imagery':
      case 'uiStyles':
      case 'cardStyles':
        return undefined;
      case 'fontFamily/collection':
      case 'fontFamily/category':
        return { path: 'fontFamily', target: this.at.target, tab: this.at.tab };
      default:
        throw new UnreachableError(this.at);
    }
  }

  goto(route: Route): void {
    this.at = route;
  }

  toggleToplevel(toplevel: Exclude<Toplevel, 'fontFamily'>): void {
    this.at =
      this.at?.path === toplevel || toplevel === 'imagery'
        ? undefined
        : { path: toplevel };
  }

  readonly close = () => {
    this.at = undefined;
  };
}
